import {
  SideNavigationV2Props,
  PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import { Step, StepStatus, iconTypes } from "skedyul-ui/ui";
import { AppLayoutState } from "@/app/[subdomain]/_schema.layout";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import type { GetWorkflowRunStepType } from "skedyul-core/features/workflows/features/workflowRuns/resources/types";
import { cancelWorkflowRun } from "@store/features/settings/workflowSlice";
import { store } from "@store/index";
import { open as openDialog } from "@store/features/layout/dialogSlice";

dayjs.extend(relativeTime);
dayjs.extend(duration);

/** Roll up subflow parent status from nested child steps (worst status wins). */
function rollUpSubflowStepStatus(
  parentStatus: string,
  subflowSteps: Array<{ status: string }> | undefined,
): string {
  if (!subflowSteps?.length) {
    return parentStatus;
  }

  const priority: Record<string, number> = {
    FAILED: 4,
    CANCELLED: 3,
    TIMED_OUT: 3,
    RUNNING: 2,
    QUEUED: 1,
    COMPLETED: 0,
    SKIPPED: 0,
  };

  let worstStatus = parentStatus;
  let worstPriority = priority[parentStatus] ?? 0;

  for (const child of subflowSteps) {
    const childPriority = priority[child.status] ?? 0;
    if (childPriority > worstPriority) {
      worstPriority = childPriority;
      worstStatus = child.status;
    }
  }

  return worstStatus;
}

// Helper function to get the appropriate timestamp for ticking
const getTimestamp = (step: {
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  status: string;
}): Date | undefined => {
  // For completed steps, use completedAt
  if (step.completedAt) {
    return new Date(step.completedAt);
  }

  // For running steps, use startedAt
  if (step.startedAt) {
    return new Date(step.startedAt);
  }

  // For scheduled steps, use scheduledAt
  if (step.scheduledAt) {
    return new Date(step.scheduledAt);
  }

  // If no timestamps available
  return undefined;
};

// Helper function to calculate wait duration
const getWaitDuration = (step: GetWorkflowRunStepType) => {
  // Need both scheduledAt and createdAt to calculate wait duration
  if (!step.scheduledAt || !step.startedWaitingAt) {
    return undefined;
  }

  const scheduledTime = dayjs(step.scheduledAt);
  const startTime = dayjs(step.startedWaitingAt);

  const diff = scheduledTime.diff(startTime);
  const waitDuration = dayjs.duration(diff);

  // Format the wait duration nicely based on how long it was
  let result;
  if (waitDuration.asSeconds() < 60) {
    result = `~${Math.round(waitDuration.asSeconds())}s wait`;
  } else if (waitDuration.asMinutes() < 60) {
    result = `${Math.round(waitDuration.asMinutes())}m wait`;
  } else if (waitDuration.asHours() < 24) {
    result = `${Math.round(waitDuration.asHours())}h wait`;
  } else {
    result = `${Math.round(waitDuration.asDays())}d wait`;
  }

  return result;
};

// Recursive function to map workflow steps including subflows
const mapWorkflowStep = (
  step: GetWorkflowRunStepType & { subflowSteps?: GetWorkflowRunStepType[] },
): Step => {
  // Parse meta data if needed
  const metaData = step.meta
    ? typeof step.meta === "string"
      ? (JSON.parse(step.meta) as {
          outputs?: unknown;
          error?: string;
          logs?: string[];
        })
      : (step.meta as { outputs?: unknown; error?: string; logs?: string[] })
    : undefined;

  // Extract response output if available - only if it doesn't have subflow steps
  let terminal;
  let terminals:
    | Array<{ label: string; lines: Array<{ value: string; date: Date }> }>
    | undefined;

  if (!step.subflowSteps?.length) {
    // Extract only response from outputs (outputs.response) and error
    const outputs = metaData?.outputs;
    const error = metaData?.error;

    // Check if outputs is an array (from loop execution)
    const isArrayOutput = Array.isArray(outputs);

    // If logs exist, show multiple terminals: logs and outputs
    if (
      metaData?.logs &&
      Array.isArray(metaData.logs) &&
      metaData.logs.length > 0
    ) {
      terminals = [];

      // Add logs terminal
      terminals.push({
        label: "Logs",
        lines: metaData.logs.map((log) => ({
          value: log,
          date: new Date(),
        })),
      });

      // Handle array outputs (from loops) - one terminal per iteration
      if (isArrayOutput && outputs.length > 0) {
        outputs.forEach((outputItem: unknown, index: number) => {
          const outputLines: Array<{ value: string; date: Date }> = [];
          const itemError =
            outputItem &&
            typeof outputItem === "object" &&
            "error" in outputItem
              ? (outputItem as { error?: string }).error
              : undefined;
          const response =
            outputItem &&
            typeof outputItem === "object" &&
            "response" in outputItem
              ? (outputItem as { response?: unknown }).response
              : undefined;

          // Show error if present
          if (itemError) {
            outputLines.push({
              value: `[ERROR] ${itemError}`,
              date: new Date(),
            });
          }

          // Show response if present
          if (response !== undefined && response !== null) {
            outputLines.push({
              value: JSON.stringify(response, null, 2),
              date: new Date(),
            });
          }

          if (outputLines.length > 0 && terminals) {
            terminals.push({
              label: `Output ${index + 1}`,
              lines: outputLines,
            });
          }
        });
      } else if (!isArrayOutput) {
        // Single output (non-array)
        const response =
          outputs && typeof outputs === "object" && "response" in outputs
            ? (outputs as { response?: unknown }).response
            : undefined;
        const outputLines: Array<{ value: string; date: Date }> = [];

        // Show error if present
        if (error) {
          outputLines.push({
            value: `[ERROR] ${error}`,
            date: new Date(),
          });
        }

        // Show response if present
        if (response !== undefined && response !== null) {
          outputLines.push({
            value: JSON.stringify(response, null, 2),
            date: new Date(),
          });
        }

        if (outputLines.length > 0) {
          terminals.push({
            label: "Output",
            lines: outputLines,
          });
        }
      }
    } else {
      // No logs - handle array outputs or single output
      if (isArrayOutput && outputs.length > 0) {
        // Array outputs - create multiple terminals
        terminals = [];

        outputs.forEach((outputItem: unknown, index: number) => {
          const lines: Array<{ value: string; date: Date }> = [];
          const itemError =
            outputItem &&
            typeof outputItem === "object" &&
            "error" in outputItem
              ? (outputItem as { error?: string }).error
              : undefined;
          const response =
            outputItem &&
            typeof outputItem === "object" &&
            "response" in outputItem
              ? (outputItem as { response?: unknown }).response
              : undefined;

          // Show error if present
          if (itemError) {
            lines.push({
              value: `[ERROR] ${itemError}`,
              date: new Date(),
            });
          }

          // Show response if present
          if (response !== undefined && response !== null) {
            lines.push({
              value: JSON.stringify(response, null, 2),
              date: new Date(),
            });
          }

          if (lines.length > 0) {
            // terminals is guaranteed to be initialized above
            terminals!.push({
              label: `# ${index + 1}`,
              lines,
            });
          }
        });
      } else if (!isArrayOutput) {
        // Single output (non-array)
        const response =
          outputs && typeof outputs === "object" && "response" in outputs
            ? (outputs as { response?: unknown }).response
            : undefined;
        const lines: Array<{ value: string; date: Date }> = [];

        // Show error if present
        if (error) {
          lines.push({
            value: `[ERROR] ${error}`,
            date: new Date(),
          });
        }

        // Show response if present
        if (response !== undefined && response !== null) {
          lines.push({
            value: JSON.stringify(response, null, 2),
            date: new Date(),
          });
        }

        if (lines.length > 0) {
          terminal = {
            label: error ? "Error" : "Output",
            lines,
          };
        }
      }
    }
  }

  // Process subflow steps if they exist
  const subflowSteps = step.subflowSteps
    ? step.subflowSteps.map(mapWorkflowStep)
    : undefined;

  const displayStatus = rollUpSubflowStepStatus(
    step.status,
    subflowSteps,
  ) as StepStatus;

  return {
    id: step.id,
    name: step.name || "Unnamed Step",
    status: displayStatus,
    timestamp: getTimestamp(step),
    duration: getWaitDuration(step),
    metadata: metaData,
    terminal,
    terminals,
    subflowSteps,
  };
};

export const runWorkflowSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const {
    workflows: {
      workflow: { data: workflowData },
      workflowRun: { data: workflowRun },
    },
    sideNavigation,
  } = args.state as AppLayoutState & {
    sideNavigation: Record<string, { isOpen?: boolean }>;
  };

  // Prefer live client state if available to reflect latest run updates after navigation
  const liveRun = store.getState().workflows.workflowRun.data || workflowRun;

  const isOpen = sideNavigation[args.position]?.isOpen ?? true;

  const runSteps = liveRun && Array.isArray(liveRun.steps) ? liveRun.steps : [];
  const uiSteps: Step[] = runSteps.map((step) => mapWorkflowStep(step));

  const isCancelled =
    liveRun?.cancelledAt !== null && liveRun?.cancelledAt !== undefined;
  const isCompleted =
    liveRun?.completedAt !== null && liveRun?.completedAt !== undefined;
  const isFailed =
    (liveRun as { failedAt?: string | null })?.failedAt !== null &&
    (liveRun as { failedAt?: string | null })?.failedAt !== undefined;
  const isStopped = isCompleted || isCancelled || isFailed;

  return {
    side: args.position,
    isOpen,
    width: args.width || "750px",
    onSidebarOpenChange: (open: boolean) => {
      if (!open) {
        const { pathname, search } = window.location;
        const params = new URLSearchParams(search);
        params.delete("run");
        const newSearch = params.toString();
        args.routerContext.router.replace(
          `${pathname}${newSearch ? `?${newSearch}` : ""}`,
        );
      }
    },
    component: {
      component: "PageV2",
      props: {
        title: workflowData?.name || "Workflow Run",
        hideTitle: true,
        content: {
          structure: "sidebar",
          components: [
            {
              type: "StepRun",
              title: "",
              props: {
                steps: uiSteps,
                actions: [
                  ...(!isCompleted && !isCancelled
                    ? [
                        {
                          label: "Cancel",
                          leftIcon: "X" as iconTypes,
                          handleClick: async () => {
                            if (workflowRun) {
                              await store.dispatch(
                                cancelWorkflowRun({
                                  workflowRunId: workflowRun.id,
                                }),
                              );
                            }
                          },
                        },
                      ]
                    : []),
                  ...(isStopped
                    ? [
                        {
                          label: "Retry",
                          leftIcon: "RotateCcw" as iconTypes,
                          handleClick: () => {
                            if (liveRun) {
                              store.dispatch(
                                openDialog({
                                  schema: "RETRY_WORKFLOW",
                                  params: {
                                    workflowId: liveRun.workflowId,
                                    workflowVersionId:
                                      liveRun.workflowVersionId,
                                    inputs: liveRun.inputs,
                                    workflowName: workflowData?.name,
                                  },
                                }),
                              );
                            }
                          },
                        },
                      ]
                    : []),
                ],
                inputs: [
                  {
                    value: JSON.stringify(liveRun?.inputs || {}),
                    date: new Date(),
                  },
                ],
                workflowName: workflowData?.name || "Workflow Run",
                compact: false,
              },
            },
          ],
        },
      },
    },
  };
};
